import { useCallback } from 'react';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import {
  CompassConfig,
  DEFAULT_COMPASS_CONFIG,
  StrategyCompassMode,
  StrategyCompassXYZAxis,
} from 'types/compass';

const CMP_QUERY_KEYS = {
  cmp_mode: parseAsStringEnum<StrategyCompassMode>(
    Object.values(StrategyCompassMode),
  ).withDefault(DEFAULT_COMPASS_CONFIG.mode),
  cmp_x: parseAsStringEnum<StrategyCompassXYZAxis>(
    Object.values(StrategyCompassXYZAxis),
  ).withDefault(DEFAULT_COMPASS_CONFIG.xAxis),
  cmp_y: parseAsStringEnum<StrategyCompassXYZAxis>(
    Object.values(StrategyCompassXYZAxis),
  ).withDefault(DEFAULT_COMPASS_CONFIG.yAxis),
  cmp_z: parseAsStringEnum<StrategyCompassXYZAxis>(
    Object.values(StrategyCompassXYZAxis),
  ).withDefault(StrategyCompassXYZAxis.None),
  cmp_syms: parseAsArrayOf(parseAsString).withDefault(
    DEFAULT_COMPASS_CONFIG.symbols,
  ),
  cmp_wids: parseAsArrayOf(parseAsInteger).withDefault(
    DEFAULT_COMPASS_CONFIG.activeWatchlistIds,
  ),
  cmp_scan: parseAsArrayOf(parseAsString).withDefault(
    DEFAULT_COMPASS_CONFIG.selectedScanners,
  ),
  cmp_wl: parseAsArrayOf(parseAsString).withDefault(
    DEFAULT_COMPASS_CONFIG.selectedWatchlists,
  ),
  cmp_hist: parseAsInteger.withDefault(DEFAULT_COMPASS_CONFIG.historyLookback),
};

export function useCompassConfig(): [
  CompassConfig,
  (patch: Partial<CompassConfig>) => void,
] {
  const [params, setParams] = useQueryStates(CMP_QUERY_KEYS);

  const config: CompassConfig = {
    mode: params.cmp_mode,
    xAxis: params.cmp_x,
    yAxis: params.cmp_y,
    zAxis:
      params.cmp_z === StrategyCompassXYZAxis.None ? undefined : params.cmp_z,
    symbols: params.cmp_syms,
    activeWatchlistIds: params.cmp_wids,
    selectedScanners: params.cmp_scan,
    selectedWatchlists: params.cmp_wl,
    historyLookback: params.cmp_hist,
  };

  const updateConfig = useCallback(
    (patch: Partial<CompassConfig>) => {
      const urlPatch: Partial<typeof params> = {};
      if (patch.mode != null) {
        urlPatch.cmp_mode = patch.mode;
      }
      if (patch.xAxis != null) {
        urlPatch.cmp_x = patch.xAxis;
      }
      if (patch.yAxis != null) {
        urlPatch.cmp_y = patch.yAxis;
      }
      if ('zAxis' in patch) {
        urlPatch.cmp_z = patch.zAxis ?? StrategyCompassXYZAxis.None;
      }
      if (patch.symbols != null) {
        urlPatch.cmp_syms = patch.symbols;
      }
      if (patch.activeWatchlistIds != null) {
        urlPatch.cmp_wids = patch.activeWatchlistIds;
      }
      if (patch.selectedScanners != null) {
        urlPatch.cmp_scan = patch.selectedScanners;
      }
      if (patch.selectedWatchlists != null) {
        urlPatch.cmp_wl = patch.selectedWatchlists;
      }
      if (patch.historyLookback != null) {
        urlPatch.cmp_hist = patch.historyLookback;
      }
      void setParams(urlPatch);
    },
    [setParams],
  );

  return [config, updateConfig];
}
